import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import AdminCreditsService from '../../../services/adminCreditsService';
import Button from '../../../components/ui/Button';
import { Wallet, Search, Filter, TrendingUp, Users, Award, DollarSign } from 'lucide-react';
import CreditsAdjustmentModal from './CreditsAdjustmentModal';
import CreditHistoryModal from './CreditHistoryModal';

const CreditsManagement = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [employers, setEmployers] = useState([]);
  const [filteredEmployers, setFilteredEmployers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [minCredits, setMinCredits] = useState('');
  const [maxCredits, setMaxCredits] = useState('');
  const [stats, setStats] = useState(null);
  const [notification, setNotification] = useState(null);
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedEmployer, setSelectedEmployer] = useState(null);

  // Subscription tiers
  const tiers = [
    { id: 'all', label: 'All Tiers' },
    { id: 'free', label: 'Free' },
    { id: 'pro', label: 'Pro' },
    { id: 'premium', label: 'Premium' },
    { id: 'agency', label: 'Agency' }
  ];

  // Check admin/moderator access
  useEffect(() => {
    if (userProfile && userProfile.role !== 'admin' && userProfile.role !== 'moderator') {
      navigate('/dashboard');
    }
  }, [userProfile, navigate]);

  // Load employers and stats
  const loadEmployers = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {
        tier: selectedTier,
        search: searchQuery,
        minCredits: minCredits,
        maxCredits: maxCredits
      };

      const result = await AdminCreditsService.getEmployersWithCredits(filters);
      if (result.success) {
        setEmployers(result.data);
        setFilteredEmployers(result.data);
      } else {
        showNotification('error', result.error || 'Failed to load employers');
      }

      // Load stats
      const statsResult = await AdminCreditsService.getCreditUsageStats();
      if (statsResult.success) {
        setStats(statsResult.data);
      }
    } catch (error) {
      console.error('Error loading employers:', error);
      showNotification('error', 'An error occurred while loading data');
    } finally {
      setLoading(false);
    }
  }, [selectedTier, searchQuery, minCredits, maxCredits]);

  useEffect(() => {
    loadEmployers();
  }, [loadEmployers]);

  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleAdjustCredits = (employer) => {
    setSelectedEmployer(employer);
    setShowAdjustmentModal(true);
  };

  const handleViewHistory = (employer) => {
    setSelectedEmployer(employer);
    setShowHistoryModal(true);
  };

  const handleCreditAdjusted = () => {
    setShowAdjustmentModal(false);
    setSelectedEmployer(null);
    loadEmployers();
    showNotification('success', 'Credits adjusted successfully');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getTierBadgeColor = (tier) => {
    const colors = {
      free: 'bg-gray-100 text-gray-700',
      pro: 'bg-blue-100 text-blue-700',
      premium: 'bg-purple-100 text-purple-700',
      agency: 'bg-yellow-100 text-yellow-700'
    };
    return colors[tier] || colors.free;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Wallet size={28} className="text-blue-600" />
              AI Credits Management
            </h1>
            <p className="text-gray-600 mt-1">Manage AI credits for employer users</p>
          </div>
        </div>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Statistics Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Employers</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalEmployers}</p>
              </div>
              <Users size={32} className="text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Credits Allocated</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCreditsAllocated}</p>
              </div>
              <Wallet size={32} className="text-green-500" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Credits Used</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCreditsUsed}</p>
              </div>
              <TrendingUp size={32} className="text-purple-500" />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Generations</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalGenerations}</p>
              </div>
              <Award size={32} className="text-yellow-500" />
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Name or email..."
                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Tier Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Tier
            </label>
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              {tiers.map((tier) => (
                <option key={tier.id} value={tier.id}>
                  {tier.label}
                </option>
              ))}
            </select>
          </div>

          {/* Min Credits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Min Credits
            </label>
            <input
              type="number"
              value={minCredits}
              onChange={(e) => setMinCredits(e.target.value)}
              placeholder="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Max Credits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Max Credits
            </label>
            <input
              type="number"
              value={maxCredits}
              onChange={(e) => setMaxCredits(e.target.value)}
              placeholder="100"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Employers Table */}
      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Subscription
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits Remaining
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Credits Used
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Generations
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredEmployers.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    No employers found
                  </td>
                </tr>
              ) : (
                filteredEmployers.map((employer) => (
                  <tr key={employer.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {employer.full_name || 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">{employer.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTierBadgeColor(employer.subscription_tier)}`}>
                        {(employer.subscription_tier || 'free').toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Wallet size={16} className="text-green-500 mr-2" />
                        <span className="text-sm font-semibold text-gray-900">
                          {employer.ai_credits_remaining || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {employer.ai_credits_used || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900">
                        {employer.ai_generation_count || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAdjustCredits(employer)}
                        >
                          Adjust Credits
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewHistory(employer)}
                        >
                          View History
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modals */}
      {showAdjustmentModal && selectedEmployer && (
        <CreditsAdjustmentModal
          employer={selectedEmployer}
          onClose={() => {
            setShowAdjustmentModal(false);
            setSelectedEmployer(null);
          }}
          onSuccess={handleCreditAdjusted}
        />
      )}

      {showHistoryModal && selectedEmployer && (
        <CreditHistoryModal
          employer={selectedEmployer}
          onClose={() => {
            setShowHistoryModal(false);
            setSelectedEmployer(null);
          }}
        />
      )}
    </div>
  );
};

export default CreditsManagement;
